/**
 * Single source of truth for print route detection (AG-003 / PRINT-01).
 *
 * Print routes render the printed document ONLY — without AppShell, headers,
 * sidebars, or navigation chrome.
 *
 * Excludes configuration/designer pages (like /settings/print-templates)
 * which must remain inside the full AppShell.
 */

export const SETTINGS_PRINT_EXCLUSIONS = [
  "/settings/print-templates",
  "/control/print-profiles",
  "/control/customization",
] as const;

export const SLIP_AND_STANDALONE_PRINT_PREFIXES = [
  "/workshop/receive-slip/",
  "/workshop/filings-slip/",
  "/workshop/material-slip/",
  "/conversion/slip/",
  "/billing/settlement-slip/",
  "/workshop/print/",
  "/orders/print/",
  "/repair/print/",
  "/stock/print/",
  "/attendance/print/",
  "/people/print/",
  "/billing/print/",
  "/platform/billing-print/",
] as const;

/**
 * Authorized PrintEngine Route Exemptions (AG-004):
 *
 * 1. `/reports/ledgers-print-all`:
 *    Continuous batch spooling print across ALL active party accounts (Accountwise Details).
 *    PrintEngine is single-record oriented (`docType` + `recordId`). Batch spooling multi-account
 *    printing compiles and spools dozens to hundreds of ledgers in a single continuous stream.
 *
 * 2. `/platform/billing-print/$id`:
 *    Dedicated AVS SaaS corporate platform billing tax invoice with isolated provider entity
 *    (Arivahly Venture Sphere Pvt Ltd, CIN U72900KA2023PTC176543, SAC 998313).
 *    Exempt to strictly isolate corporate subscription invoices from tenant jewellery shop branding.
 */
export const AUTHORIZED_PRINT_ENGINE_EXEMPTIONS = [
  "/reports/ledgers-print-all",
  "/platform/billing-print/",
] as const;

export function isPrintRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;

  // 1. Settings, designers, and hubs must keep AppShell
  if (SETTINGS_PRINT_EXCLUSIONS.some((ex) => pathname.startsWith(ex))) {
    return false;
  }

  // 2. Exact slip and dedicated print document prefixes
  if (SLIP_AND_STANDALONE_PRINT_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  // 3. Substring patterns for standardized print documents
  if (
    pathname.includes("-print") ||
    pathname.includes("/print/") ||
    pathname.endsWith("/print") ||
    pathname.includes("print-log")
  ) {
    return true;
  }

  return false;
}
